import { accentRampFrom, applyBranding, contrast, luminanceFromOklch, oklchFromHex } from "./branding";

/**
 * The three-colour system of a consumer-facing page (TDD-SA FR-SA-198 and FR-SA-201; design note
 * `docs/design/salon-booking-organic-wellness.md` section 5): a ground, an ink and an accent.
 *
 * The owner's Organic Wellness style (2026-10-28, decision W1) fixes two of the three for every salon:
 * a cream ground and a forest-green ink, whose lighter steps are the sage tones. The third, the accent,
 * is the salon's own brand colour; with none set it is the board's terracotta. Only the accent rows
 * move with the brand, so every neutral pair keeps the board's exact colours and contrast.
 *
 * Dark mode is the board's own dark bands (decision W2): the forest green becomes the ground, the cream
 * the text, and the accent its soft step, lifted as the board lifts #a3532f to #e09a7b.
 *
 * `palette.test.ts` holds every pair to WCAG over awkward brand colours: headings at 7:1, text and
 * labels at 4.5:1.
 */
export interface PaletteMode {
  /** The page. */
  ground: string;
  /** Bands and cards on the ground. */
  surface: string;
  /** Sheets, forms and pop-overs above the surface. */
  raised: string;
  /** A recessed well: skeletons, inputs, quiet chips, hovers. */
  sunken: string;
  /** Headings, ink panels, selected states. */
  ink: string;
  /** Body text. */
  content: string;
  /** Secondary text and labels. */
  subtle: string;
  /** The quietest text that still reads at 4.5:1. */
  muted: string;
  /** Outlines on secondary buttons. */
  edge: string;
  /** Hairlines and field borders. */
  edgeSubtle: string;
  edgeMuted: string;
  /** The green's bright step: dots, the leaf mark, chosen chips. */
  sage: string;
  /** The green's pale step: the arrow disc, today's hours, soft fills. */
  sageTint: string;
  /** The brand colour (light) or its soft step (dark), nudged only if its label would not read: primary actions, highlights. */
  accent: string;
  onAccent: string;
  /** The accent's hue as text: readable on the ground, the surface and the tint. */
  accentText: string;
  /**
   * Large display text in the accent (a headline's italic words, the stars): the accent itself where it
   * reads at 3:1 on the ground, as large text needs, else the readable accent text. Lime on white, say.
   */
  highlight: string;
  /** A wash of the accent: blush cards, a selection. */
  tint: string;
}

export interface Palette {
  light: PaletteMode;
  dark: PaletteMode;
}

/** The booking site's colour when nobody set one (design note section 3): the board's terracotta. */
export const DEFAULT_BRAND_COLOUR = "#a3532f";

interface Oklch {
  l: number;
  c: number;
  h: number;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

/** Four places, so the board's colours come back to the same hex in the browser. */
function format({ l, c, h }: Oklch): string {
  return `oklch(${l.toFixed(4)} ${c.toFixed(4)} ${h.toFixed(2)})`;
}

/** Parses the `oklch(l c h)` strings `accentRampFrom` writes. */
function parse(value: string): Oklch {
  const [l, c, h] = /^oklch\(([\d.]+) ([\d.]+) ([\d.]+)\)$/.exec(value)?.slice(1).map(Number) ?? [0, 0, 0];
  return { l: l ?? 0, c: c ?? 0, h: h ?? 0 };
}

const at = oklchFromHex;

/** The board's fixed tones, read off its source (design note section 5). */
const LIGHT = {
  ground: at("#f3ece0"),
  surface: at("#e9e2d3"),
  raised: at("#fbf7f0"),
  sunken: at("#e4e8d6"),
  ink: at("#23362b"),
  content: at("#3e4f43"),
  subtle: at("#4f6345"),
  muted: at("#56684d"),
  edge: at("#9caf88"),
  edgeSubtle: at("#c9d4b8"),
  edgeMuted: at("#dde2cd"),
  sage: at("#6f8460"),
  sageTint: at("#c9d4b8"),
};

const DARK = {
  ground: at("#23362b"),
  surface: at("#2b4033"),
  raised: at("#34493b"),
  sunken: at("#2b4033"),
  ink: at("#f3ece0"),
  content: at("#d8d9cb"),
  subtle: at("#c9d4b8"),
  muted: at("#aeb8a2"),
  edge: at("#6f8460"),
  edgeSubtle: at("#4f6345"),
  edgeMuted: at("#3e5244"),
  sage: at("#9caf88"),
  sageTint: at("#3e5244"),
};

/** The board's terracotta and the two tones it makes from it: the soft dark step and the blush. */
const TERRACOTTA = at(DEFAULT_BRAND_COLOUR);
const SOFT = at("#e09a7b");
const BLUSH = at("#f1dccf");

/**
 * `anchor` stepped from `brand` as the board steps it from the terracotta: the same share of chroma and
 * turn of hue. The lightness is the anchor's own (`fixed`, for a wash that text sits on) or the same lift,
 * never below the anchor's (for the soft dark accent, so a near-black brand still glows on the green).
 */
function stepped(brand: Oklch, anchor: Oklch, grey: boolean, fixed: boolean): Oklch {
  const lifted = Math.max(anchor.l, brand.l + anchor.l - TERRACOTTA.l);
  return {
    l: Math.min(0.95, fixed ? anchor.l : lifted),
    c: grey ? 0 : brand.c * (anchor.c / TERRACOTTA.c),
    h: grey ? brand.h : (brand.h + anchor.h - TERRACOTTA.h + 360) % 360,
  };
}

const lum = ({ l, c, h }: Oklch) => luminanceFromOklch(l, c, h);

/** Whichever of the two reads better on `fill`. */
function readableOn(fill: Oklch, one: Oklch, two: Oklch): Oklch {
  return contrast(lum(fill), lum(one)) >= contrast(lum(fill), lum(two)) ? one : two;
}

/**
 * A button fill and its label: the label is whichever of the two reads better, and a mid-tone fill that
 * neither reaches 4.5:1 on is stepped away from its label until it does. The brand colour stays as it
 * is everywhere else; only the fill under a label moves, and only as far as it must.
 */
function filled(fill: Oklch, light: Oklch, dark: Oklch): { fill: Oklch; label: Oklch } {
  const label = readableOn(fill, light, dark);
  const lighter = lum(label) < lum(fill);
  let next = fill;
  while (contrast(lum(next), lum(label)) < 4.5 && next.l > 0.05 && next.l < 0.99) {
    next = { ...next, l: next.l + (lighter ? 0.005 : -0.005) };
  }
  return { fill: next, label };
}

/** Steps `text` darker (or lighter) until it reads at 4.5:1 on every one of `grounds`. */
function readableText(text: Oklch, grounds: readonly Oklch[], darker: boolean): Oklch {
  // A white or black brand starts inside the range the loop can move in.
  let next = { ...text, l: Math.min(0.98, Math.max(0.06, text.l)) };
  // A hair over 4.5, so the four-place rounding of the written value can never take it under.
  const reads = (t: Oklch) => grounds.every((g) => contrast(lum(t), lum(g)) >= 4.52);
  while (!reads(next) && next.l > 0.05 && next.l < 0.99) {
    next = { ...next, l: next.l + (darker ? -0.005 : 0.005) };
  }
  return next;
}

/** The accent for large text: itself when it reads at 3:1 on the ground (WCAG large text), else `text`. */
function highlightOf(accent: Oklch, ground: Oklch, text: Oklch): Oklch {
  return contrast(lum(accent), lum(ground)) >= 3.05 ? accent : text;
}

/** The fixed tones of one mode as `oklch()` strings. */
function neutrals(tones: typeof LIGHT): Omit<PaletteMode, "accent" | "onAccent" | "accentText" | "tint" | "highlight"> {
  return {
    ground: format(tones.ground),
    surface: format(tones.surface),
    raised: format(tones.raised),
    sunken: format(tones.sunken),
    ink: format(tones.ink),
    content: format(tones.content),
    subtle: format(tones.subtle),
    muted: format(tones.muted),
    edge: format(tones.edge),
    edgeSubtle: format(tones.edgeSubtle),
    edgeMuted: format(tones.edgeMuted),
    sage: format(tones.sage),
    sageTint: format(tones.sageTint),
  };
}

/**
 * The colour themes a salon may pick (TDD-SA FR-SA-204; design note salon-booking-organic-wellness.md
 * section 11): Organic Wellness, the default, then the five palettes of the owner's "Salon Website
 * Styles 2" board and the five of its "Salon Website Explore Styles" board, as the boards give them.
 * Only the colours differ; the layout and the fonts do not.
 */
export const COLOUR_THEMES = [
  { name: "Organic Wellness", ground: "#f3ece0", ink: "#23362b", accent: "#a3532f", darkGround: "#23362b", darkAccent: "#e09a7b" },
  { name: "Rosé Arch", ground: "#f8ece8", ink: "#3a2420", accent: "#b85c5c", darkGround: "#1f1513", darkAccent: "#e79a91" },
  { name: "Runway Noir", ground: "#f2f2ee", ink: "#0b0b0b", accent: "#d4ff3a", darkGround: "#0b0b0b", darkAccent: "#d4ff3a" },
  { name: "Golden Hour", ground: "#f4ecdc", ink: "#0f3a32", accent: "#a47a2c", darkGround: "#0c2a24", darkAccent: "#d4ad62" },
  { name: "Lilac Studio", ground: "#ece6ff", ink: "#1b1340", accent: "#ff5a4e", darkGround: "#120c2b", darkAccent: "#ff7a6e" },
  { name: "Indigo Silk", ground: "#f5efe4", ink: "#1b1c4a", accent: "#c5781a", darkGround: "#1b1c4a", darkAccent: "#efa53c" },
  { name: "Emerald Rose", ground: "#cfe8dc", ink: "#0b3b2c", accent: "#d6456e", darkGround: "#0b3b2c", darkAccent: "#ff8fab" },
  { name: "Cobalt Butter", ground: "#dde3ff", ink: "#0f1f86", accent: "#2340d8", darkGround: "#101d7a", darkAccent: "#ffd84d" },
  { name: "Fuchsia Soirée", ground: "#ffd6e8", ink: "#4a0630", accent: "#c8006a", darkGround: "#3d0628", darkAccent: "#ff4fa3" },
  { name: "Lemon & Tomato", ground: "#ffe14d", ink: "#161616", accent: "#e3302b", darkGround: "#161616", darkAccent: "#ff5a4f" },
  { name: "Terracotta Sun", ground: "#f7c8a8", ink: "#4a1d0c", accent: "#c2410c", darkGround: "#3b1409", darkAccent: "#ff8a4c" },
] as const;

export type ColourTheme = (typeof COLOUR_THEMES)[number];

/** The theme a salon has when it picked none: today's Organic Wellness, coloured by its brand colour. */
export const DEFAULT_COLOUR_THEME = "Organic Wellness";

/** A theme by its name, or null for Organic Wellness or a name this build does not know. */
function themeNamed(name: string | null | undefined): ColourTheme | null {
  const theme = COLOUR_THEMES.find((item) => item.name === name);
  return theme && theme.name !== DEFAULT_COLOUR_THEME ? theme : null;
}

/** The accent a page is painted with: the theme's own, or with Organic Wellness the salon's brand colour. */
export function accentOf(brand: string, theme?: string | null): string {
  return themeNamed(theme)?.accent ?? (HEX.test(brand) ? brand : DEFAULT_BRAND_COLOUR);
}

/** `t` of the way from `a` to `b` in oklch, the hue the short way round; a grey end lends no hue. */
function mix(a: Oklch, b: Oklch, t: number): Oklch {
  const hue = a.c < 0.01 ? b.h : b.c < 0.01 ? a.h : a.h + ((((b.h - a.h + 540) % 360) - 180) * t);
  return { l: a.l + (b.l - a.l) * t, c: a.c + (b.c - a.c) * t, h: (hue + 360) % 360 };
}

const WHITE: Oklch = { l: 1, c: 0, h: 0 };

/**
 * One of the board's five themes, both modes. Every role is a step between the theme's ground and its
 * ink: cards, wells and hairlines step from the ground towards the ink, text from the ink towards the
 * ground, the secondary ("sage") roles are the ink's middle and pale steps, and the tint is a wash of
 * the accent. Dark is the dark ground with the light ground as its text. Text and labels are nudged
 * only as far as 4.5:1 needs.
 */
function themedPalette(theme: ColourTheme): Palette {
  const mode = (ground: Oklch, ink: Oklch, accent: Oklch, light: boolean): PaletteMode => {
    const surface = mix(ground, ink, light ? 0.05 : 0.06);
    const raised = light ? mix(ground, WHITE, 0.55) : mix(ground, ink, 0.11);
    const sunken = mix(ground, ink, light ? 0.09 : 0.035);
    const button = filled(accent, light ? ground : ink, light ? ink : ground);
    const tint = mix(ground, accent, 0.16);
    const grounds = [ground, surface, raised, sunken];
    const text = (t: number) => readableText(mix(ink, ground, t), grounds, light);
    const accentText = readableText(accent, [ground, surface, raised, tint], light);
    return {
      ground: format(ground),
      surface: format(surface),
      raised: format(raised),
      sunken: format(sunken),
      ink: format(ink),
      content: format(text(light ? 0.12 : 0.1)),
      subtle: format(text(light ? 0.28 : 0.24)),
      muted: format(text(light ? 0.36 : 0.34)),
      edge: format(mix(ground, ink, light ? 0.32 : 0.26)),
      edgeSubtle: format(mix(ground, ink, light ? 0.18 : 0.17)),
      edgeMuted: format(mix(ground, ink, light ? 0.11 : 0.12)),
      sage: format(mix(ink, ground, 0.42)),
      sageTint: format(mix(ground, ink, 0.16)),
      accent: format(button.fill),
      onAccent: format(button.label),
      accentText: format(accentText),
      highlight: format(highlightOf(accent, ground, accentText)),
      tint: format(tint),
    };
  };
  const ground = oklchFromHex(theme.ground);
  return {
    light: mode(ground, oklchFromHex(theme.ink), oklchFromHex(theme.accent), true),
    dark: mode(oklchFromHex(theme.darkGround), ground, oklchFromHex(theme.darkAccent), false),
  };
}

/**
 * Both modes of a salon's page. With a theme from the board (FR-SA-204) the theme sets every colour;
 * with Organic Wellness, the default, the brand colour moves only the accent rows. An invalid brand
 * colour gives the default.
 */
export function paletteFrom(brand: string, theme?: string | null): Palette {
  const chosen = themeNamed(theme);
  return chosen ? themedPalette(chosen) : organicPalette(brand);
}

/** Organic Wellness: the fixed cream, green and sage, with the brand colour as the accent. */
function organicPalette(brand: string): Palette {
  const hex = HEX.test(brand) ? brand : DEFAULT_BRAND_COLOUR;
  const own = oklchFromHex(hex);
  const ramp = accentRampFrom(hex);
  // A grey brand has no hue to lend: its tints stay grey rather than drifting pink at hue 0.
  const grey = own.c < 0.02;

  const lightButton = filled(own, LIGHT.ground, LIGHT.ink);
  const lightTint = stepped(own, BLUSH, grey, true);

  const soft = grey ? parse(ramp.bright) : stepped(own, SOFT, grey, false);
  const darkButton = filled(soft, DARK.ink, DARK.ground);
  const darkAccent = darkButton.fill;
  // A wash just above the green, so the soft accent still reads as text on it.
  const darkTint: Oklch = { l: DARK.ground.l + 0.035, c: 0.3 * darkAccent.c, h: darkAccent.h };

  // The brand itself as text, darkened only as far as small text needs to read on cream and blush.
  const lightText = readableText(own, [LIGHT.ground, LIGHT.surface, LIGHT.raised, lightTint], true);
  const darkText = readableText(darkAccent, [DARK.ground, DARK.surface, DARK.raised, darkTint], false);

  return {
    light: {
      ...neutrals(LIGHT),
      accent: format(lightButton.fill),
      onAccent: format(lightButton.label),
      accentText: format(lightText),
      highlight: format(highlightOf(lightButton.fill, LIGHT.ground, lightText)),
      tint: format(lightTint),
    },
    dark: {
      ...neutrals(DARK),
      accent: format(darkAccent),
      onAccent: format(darkButton.label),
      accentText: format(darkText),
      highlight: format(highlightOf(darkAccent, DARK.ground, darkText)),
      tint: format(darkTint),
    },
  };
}

const ROLES: readonly (keyof PaletteMode)[] = [
  "ground",
  "surface",
  "raised",
  "sunken",
  "ink",
  "content",
  "subtle",
  "muted",
  "edge",
  "edgeSubtle",
  "edgeMuted",
  "sage",
  "sageTint",
  "accent",
  "onAccent",
  "accentText",
  "highlight",
  "tint",
];

/** `edgeSubtle` → `edge-subtle`. */
const kebab = (role: string) => role.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);

/**
 * Writes the palette onto an element as `--pal-light-<role>` and `--pal-dark-<role>`, and the
 * accent scale as `applyBranding` does, for an app's stylesheet to read into its tokens. Returns a
 * function that puts back what was there.
 */
export function applyPalette(brand: string, root: HTMLElement, theme?: string | null): () => void {
  const palette = paletteFrom(brand, theme);
  // The accent scale follows the colour the page is actually painted with: the theme's, or the brand's.
  const undoBranding = applyBranding(accentOf(brand, theme), root);
  const entries = (["light", "dark"] as const).flatMap((mode) =>
    ROLES.map((role) => [`--pal-${mode}-${kebab(role)}`, palette[mode][role]] as const),
  );
  const previous = entries.map(([name]) => [name, root.style.getPropertyValue(name)] as const);
  for (const [name, value] of entries) root.style.setProperty(name, value);

  return () => {
    for (const [name, value] of previous) {
      if (value) root.style.setProperty(name, value);
      else root.style.removeProperty(name);
    }
    undoBranding();
  };
}
